import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';

// Submit + cancel pair styled to match StockSupplierModal's footer.
// `loading` covers parent-driven async (mutations); `submitted` displays
// the green saved feedback briefly, mirroring v1 CgForm.
// Labels default to undefined so callers passing explicit (already-localized)
// labels are unaffected; otherwise they resolve to shared common keys.
export default function FormActions({
  submitLabel,
  cancelLabel,
  onCancel,
  loading = false,
  submitted = false,
  align = 'end',
  extraActions,
  disabled = false,
}) {
  const { t } = useTranslation('common');
  const { formState } = useFormContext();
  const isSubmitting = loading || formState.isSubmitting;
  const isDisabled = disabled || isSubmitting;

  const submit = submitLabel ?? t('actions.save', 'Save');
  const cancel = cancelLabel ?? t('actions.cancel', 'Cancel');

  const [showSaved, setShowSaved] = useState(false);
  useEffect(() => {
    if (!submitted) return;
    setShowSaved(true);
    const timer = setTimeout(() => setShowSaved(false), 3000);
    return () => clearTimeout(timer);
  }, [submitted]);

  const justifyClass =
    align === 'end' ? 'justify-end' : align === 'start' ? 'justify-start' : 'justify-between';

  return (
    <div className={`flex items-center gap-3 ${justifyClass} pt-2`}>
      {extraActions}
      {onCancel ? (
        <CgRadixButton
          variant="secondary"
          size="lg"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {cancel}
        </CgRadixButton>
      ) : null}
      <CgRadixButton
        type="submit"
        variant="primary"
        size="lg"
        icon={faSave}
        loading={isSubmitting}
        loadingLabel={t('actions.saving', 'Saving…')}
        disabled={isDisabled}
      >
        {submit}
      </CgRadixButton>
      {showSaved && !isSubmitting ? (
        <span className="inline-flex items-center gap-1 text-sm text-green-600">
          <FontAwesomeIcon icon={faCheckCircle} />
          {t('actions.saved', 'Saved')}
        </span>
      ) : null}
    </div>
  );
}
